/**
 * Shared aura filtering and sorting utilities (#1152).
 *
 * Consolidates the common aura sorting (soonest-expiring first, permanent last,
 * deterministic tie-breaking), polarity splitting (buff vs debuff), and visible
 * aura filtering across client HUD surfaces (SelfAuraBar, NameplateAuras, MajorAuraRow).
 *
 * PURE CLIENT-SIDE LOGIC. Reads `WireAura` and sim tick. Touches no sim/protocol/wire state.
 */

import type { WireAura } from "@pit/protocol";
import {
  auraCategory,
  auraCategoryPriority,
  type AuraCategory,
} from "../champions/auraIcons.js";

/**
 * Deterministic comparison for aura display ordering:
 * 1. Soonest-expiring first (lowest remaining ticks); permanent (no-expiry) last.
 * 2. Ties broken by def id, then source seat, then applied tick.
 *
 * A TOTAL order, so render order never wobbles between equal auras.
 */
export function compareAurasByExpiry(a: WireAura, b: WireAura, now: number): number {
  const aPermanent = a.expiresAtTick === null;
  const bPermanent = b.expiresAtTick === null;
  if (aPermanent !== bPermanent) return aPermanent ? 1 : -1;
  if (a.expiresAtTick !== null && b.expiresAtTick !== null) {
    const aRemaining = a.expiresAtTick - now;
    const bRemaining = b.expiresAtTick - now;
    if (aRemaining !== bRemaining) return aRemaining - bRemaining;
  }
  if (a.def !== b.def) return a.def < b.def ? -1 : 1;
  if (a.sourceSeat !== b.sourceSeat) return a.sourceSeat - b.sourceSeat;
  return a.appliedAtTick - b.appliedAtTick;
}

/**
 * Deterministic comparison for major auras (curated HUD displays):
 * 1. Category priority first (CC -> defensive -> debuff -> minor).
 * 2. Expiry order (soonest-expiring first, permanent last).
 * 3. Ties broken by def id, source seat, applied tick.
 */
export function compareMajorAuras(a: WireAura, b: WireAura, now: number): number {
  const ap = auraCategoryPriority(auraCategory(a.def));
  const bp = auraCategoryPriority(auraCategory(b.def));
  if (ap !== bp) return ap - bp;
  return compareAurasByExpiry(a, b, now);
}

/**
 * True when this aura's curation category is in the selected category set.
 */
export function isAuraInCategory(
  aura: WireAura,
  categories: readonly AuraCategory[],
): boolean {
  return categories.includes(auraCategory(aura.def));
}

/**
 * Sort active auras by expiration: soonest-expiring first, permanent last.
 *
 * Returns a new sorted shallow copy without mutating the input array.
 */
export function sortAuras(
  auras: readonly WireAura[],
  now: number,
): WireAura[];
/**
 * Sort a mutable active auras array in-place by expiration: soonest-expiring first, permanent last.
 *
 * Avoids allocating an extra shallow array copy when operating on fresh partitions.
 */
export function sortAuras(
  auras: WireAura[],
  now: number,
  inPlace: true,
): WireAura[];
export function sortAuras(
  auras: readonly WireAura[] | WireAura[],
  now: number,
  inPlace?: boolean,
): WireAura[] {
  const target = inPlace === true ? (auras as WireAura[]) : [...auras];
  target.sort((a, b) => compareAurasByExpiry(a, b, now));
  return target;
}

/**
 * Partition auras into buff and debuff arrays based on wire-carried polarity.
 * Does not sort by default (use `sortAuras` or `selfBarAuras` to sort).
 */
export function splitAurasByPolarity(auras: readonly WireAura[]): {
  readonly buffs: WireAura[];
  readonly debuffs: WireAura[];
} {
  const buffs: WireAura[] = [];
  const debuffs: WireAura[] = [];
  for (const aura of auras) {
    if (aura.polarity === "buff") {
      buffs.push(aura);
    } else {
      debuffs.push(aura);
    }
  }
  return { buffs, debuffs };
}

export interface AuraFilterOptions {
  /** Current sim tick for remaining duration computation. */
  readonly now: number;
  /** Filter to a subset of aura categories (e.g. major auras). */
  readonly categories?: readonly AuraCategory[] | undefined;
  /** Filter to a specific polarity ("buff" or "debuff"). */
  readonly polarity?: "buff" | "debuff" | undefined;
  /** Maximum number of auras to return after sorting. */
  readonly max?: number | undefined;
  /** Sort strategy: "expiry" (soonest-expiring first) or "category" (category priority first). Defaults to "expiry". */
  readonly sortBy?: "expiry" | "category" | undefined;
}

/**
 * Pure filter and sort function for visible aura strips/rows.
 */
export function filterVisibleAuras(
  auras: readonly WireAura[],
  options: AuraFilterOptions,
): WireAura[] {
  let result = auras.filter((a) => {
    if (options.polarity !== undefined && a.polarity !== options.polarity) {
      return false;
    }
    if (options.categories !== undefined && !isAuraInCategory(a, options.categories)) {
      return false;
    }
    return true;
  });

  const sortBy = options.sortBy ?? "expiry";
  if (sortBy === "category") {
    result.sort((a, b) => compareMajorAuras(a, b, options.now));
  } else {
    result.sort((a, b) => compareAurasByExpiry(a, b, options.now));
  }

  if (options.max !== undefined) {
    result = result.slice(0, Math.max(0, options.max));
  }

  return result;
}
